import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Search, Plus, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";
import QueryFormDialog from "@/components/QueryFormDialog";
import QueryDetailDrawer from "@/components/QueryDetailDrawer";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle
} from "@/components/ui/alert-dialog";

const TYPES = ["All", "View", "Procedure", "Function", "Module Query", "Report", "Agent", "Others"];
const PRODUCTS = ["All", "Focus 6", "Focus 7", "Focus 9/X", "Apps", "Others"];
const COMPLEXITIES = ["All", "High", "Medium", "Low"];

const complexityBadge = (c) => {
  if (c === "High") return "bg-red-50 text-red-700 border-red-200";
  if (c === "Medium") return "bg-amber-50 text-amber-700 border-amber-200";
  return "bg-emerald-50 text-emerald-700 border-emerald-200";
};

const productBadge = "bg-blue-50 text-blue-700 border-blue-200";

export default function Repository() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";

  const [queries, setQueries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filterType, setFilterType] = useState("All");
  const [filterProduct, setFilterProduct] = useState("All");
  const [filterComplexity, setFilterComplexity] = useState("All");

  const [selectedQuery, setSelectedQuery] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const [formOpen, setFormOpen] = useState(false);
  const [editingQuery, setEditingQuery] = useState(null);

  const [deleteTarget, setDeleteTarget] = useState(null);

  const fetchQueries = async () => {
    setLoading(true);
    try {
      const params = {};
      if (search) params.search = search;
      if (filterType !== "All") params.type = filterType;
      if (filterProduct !== "All") params.product = filterProduct;
      if (filterComplexity !== "All") params.complexity = filterComplexity;
      const { data } = await api.get("/queries", { params });
      setQueries(data);
    } catch (e) {
      toast.error("Failed to load queries");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const timer = setTimeout(fetchQueries, 300);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, filterType, filterProduct, filterComplexity]);

  const openDetail = (q) => {
    setSelectedQuery(q);
    setDrawerOpen(true);
  };

  const handleEdit = (q) => {
    setEditingQuery(q);
    setFormOpen(true);
    setDrawerOpen(false);
  };

  const handleAdd = () => {
    setEditingQuery(null);
    setFormOpen(true);
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await api.delete(`/queries/${deleteTarget.id}`);
      toast.success("Query deleted");
      setDeleteTarget(null);
      setDrawerOpen(false);
      fetchQueries();
    } catch (e) {
      toast.error("Failed to delete");
    }
  };

  const onFormSuccess = (savedQuery) => {
    setFormOpen(false);
    setEditingQuery(null);
    fetchQueries();
    if (savedQuery) {
      setSelectedQuery(savedQuery);
    }
  };

  return (
    <div className="p-8 max-w-[1600px]" data-testid="repository-page">
      <div className="flex items-start justify-between mb-6">
        <div>
          <div className="text-xs font-bold uppercase tracking-widest text-blue-700 mb-2">Repository</div>
          <h1 className="font-heading text-3xl font-black tracking-tight text-slate-900">SQL Query Index</h1>
          <p className="text-sm text-slate-500 mt-1">
            {queries.length} {queries.length === 1 ? "query" : "queries"}
            {search || filterType !== "All" || filterProduct !== "All" || filterComplexity !== "All" ? " (filtered)" : ""}
          </p>
        </div>
        {isAdmin && (
          <Button onClick={handleAdd} className="bg-blue-700 hover:bg-blue-800 text-white" data-testid="add-query-button">
            <Plus className="w-4 h-4 mr-2" /> Add Query
          </Button>
        )}
      </div>

      {/* Search + Filters */}
      <div className="bg-white border border-slate-200 rounded-md p-4 mb-4">
        <div className="flex flex-col lg:flex-row gap-3">
          <div className="relative flex-1 min-w-0">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              placeholder="Search across title, purpose, SQL content, notes…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10 h-10 border-slate-200"
              data-testid="search-input"
            />
          </div>
          <div className="grid grid-cols-3 gap-3 lg:flex lg:gap-3">
            <Select value={filterType} onValueChange={setFilterType}>
              <SelectTrigger className="w-full lg:w-[160px] h-10 border-slate-200" data-testid="filter-type-select">
                <SelectValue placeholder="Type" />
              </SelectTrigger>
              <SelectContent>
                {TYPES.map((t) => (
                  <SelectItem key={t} value={t} data-testid={`filter-type-${t}`}>{t === "All" ? "All Types" : t}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={filterProduct} onValueChange={setFilterProduct}>
              <SelectTrigger className="w-full lg:w-[160px] h-10 border-slate-200" data-testid="filter-product-select">
                <SelectValue placeholder="Product" />
              </SelectTrigger>
              <SelectContent>
                {PRODUCTS.map((p) => (
                  <SelectItem key={p} value={p}>{p === "All" ? "All Products" : p}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={filterComplexity} onValueChange={setFilterComplexity}>
              <SelectTrigger className="w-full lg:w-[160px] h-10 border-slate-200" data-testid="filter-complexity-select">
                <SelectValue placeholder="Complexity" />
              </SelectTrigger>
              <SelectContent>
                {COMPLEXITIES.map((c) => (
                  <SelectItem key={c} value={c}>{c === "All" ? "All Complexity" : c}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
        <Table data-testid="queries-table">
          <TableHeader>
            <TableRow className="bg-slate-50 hover:bg-slate-50 border-slate-200">
              <TableHead className="text-xs font-bold uppercase tracking-wider text-slate-500">Title</TableHead>
              <TableHead className="text-xs font-bold uppercase tracking-wider text-slate-500">Project</TableHead>
              <TableHead className="text-xs font-bold uppercase tracking-wider text-slate-500">Type</TableHead>
              <TableHead className="text-xs font-bold uppercase tracking-wider text-slate-500">Product</TableHead>
              <TableHead className="text-xs font-bold uppercase tracking-wider text-slate-500">Complexity</TableHead>
              <TableHead className="text-xs font-bold uppercase tracking-wider text-slate-500">Version</TableHead>
              <TableHead className="text-xs font-bold uppercase tracking-wider text-slate-500 text-right">Updated</TableHead>
              {isAdmin && <TableHead className="text-xs font-bold uppercase tracking-wider text-slate-500 text-right">Actions</TableHead>}
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading && (
              <TableRow>
                <TableCell colSpan={isAdmin ? 8 : 7} className="text-center py-12 text-sm text-slate-500">
                  Loading queries…
                </TableCell>
              </TableRow>
            )}
            {!loading && queries.length === 0 && (
              <TableRow>
                <TableCell colSpan={isAdmin ? 8 : 7} className="text-center py-16">
                  <div className="text-sm text-slate-500 mb-1">No queries found</div>
                  <div className="text-xs text-slate-400">
                    {isAdmin ? "Click \"Add Query\" to create your first one." : "Ask an admin to add queries."}
                  </div>
                </TableCell>
              </TableRow>
            )}
            {!loading && queries.map((q) => (
              <TableRow
                key={q.id}
                onClick={() => openDetail(q)}
                className="cursor-pointer hover:bg-slate-50 border-slate-200"
                data-testid={`query-row-${q.id}`}
              >
                <TableCell className="font-medium text-slate-900 max-w-[320px] truncate">{q.title}</TableCell>
                <TableCell className="text-xs text-slate-600 max-w-[160px] truncate font-medium">
                  {q.project ? q.project : <span className="text-slate-400 italic">—</span>}
                </TableCell>
                <TableCell>
                  <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-slate-100 text-slate-700 border border-slate-200">
                    {q.type}
                  </span>
                </TableCell>
                <TableCell>
                  <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium border ${productBadge}`}>
                    {q.product}
                  </span>
                </TableCell>
                <TableCell>
                  <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium border ${complexityBadge(q.complexity)}`}>
                    {q.complexity}
                  </span>
                </TableCell>
                <TableCell className="font-mono text-xs text-slate-600">v{q.current_version}</TableCell>
                <TableCell className="text-right font-mono text-xs text-slate-500">
                  {new Date(q.updated_at).toLocaleDateString()}
                </TableCell>
                {isAdmin && (
                  <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                    <div className="flex items-center justify-end gap-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-8 w-8 p-0 text-slate-500 hover:text-blue-700"
                        onClick={() => handleEdit(q)}
                        data-testid={`edit-query-${q.id}`}
                      >
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-8 w-8 p-0 text-slate-500 hover:text-red-700"
                        onClick={() => setDeleteTarget(q)}
                        data-testid={`delete-query-${q.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <QueryDetailDrawer
        open={drawerOpen}
        onOpenChange={setDrawerOpen}
        query={selectedQuery}
        isAdmin={isAdmin}
        onEdit={handleEdit}
        onDelete={(q) => setDeleteTarget(q)}
      />

      <QueryFormDialog
        open={formOpen}
        onOpenChange={(o) => { setFormOpen(o); if (!o) setEditingQuery(null); }}
        editingQuery={editingQuery}
        onSuccess={onFormSuccess}
      />

      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent data-testid="delete-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete query?</AlertDialogTitle>
            <AlertDialogDescription>
              You are about to permanently delete <span className="font-medium text-slate-900">{deleteTarget?.title}</span>.
              This will remove all {deleteTarget?.current_version || 0} version(s). This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-cancel-button">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="delete-confirm-button"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
